// Background.jsx

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

// Context
import { ThemeMode, useTheme } from 'context/ThemeContext';

// Utilities
import { hexToRgba } from 'utilities/Colors';

// Services
import { resolveImageUrl } from 'services/storage/StorageAccessService';

// CSS
import './Background.css';

/**
 * Background
 *
 * Displays the shared ambient app background glow and optional blurred image layer.
 *
 * @param {object|null|undefined} node - The node used to derive the background image URL.
 *
 * @returns {ReactNode} The rendered component.
 */
const Background = React.memo(({ node }) => {

  // ----- CONTEXT ----- //
  
  // Theme Context
  const { theme, themeMode } = useTheme();
  
  // ----- STATE ----- //

  // Resolved Source State
  const [resolvedSrc, setResolvedSrc] = useState(null);

  // Active Source State
  const [activeSrc, setActiveSrc] = useState(null);

  // Previous Source Reference
  const previousUrlRef = useRef(null);

  // ----- UTILITIES ----- //

  const sourceUrl =
    themeMode === ThemeMode.DARK
      ? (
        typeof node?.coverUrl === 'string' && node.coverUrl.trim().length > 0
          ? node.coverUrl
          : (node?.type === 'PHOTO' && typeof node?.url === 'string' ? node.url : null)
      )
      : null;

  // ----- EFFECTS ----- //

  // Clears the current source pair when the source URL changes.
  useLayoutEffect(() => {
    setResolvedSrc(null);
    setActiveSrc(null);
  }, [sourceUrl]);

  // Resolves a renderable URL from storage-aware sources.
  useEffect(() => {
    let cancelled = false;

    (async () => {
      const raw = typeof sourceUrl === 'string' ? sourceUrl : '';

      if (!raw) {
        setResolvedSrc(null);
        return;
      }

      if (raw.startsWith('blob:') || raw.startsWith('data:')) {
        setResolvedSrc(raw);
        return;
      }

      try {
        const signed = await resolveImageUrl({ nodeKey: null, url: raw });
        if (!cancelled) setResolvedSrc(signed || raw);
      } catch (e) {
        if (!cancelled) setResolvedSrc(raw);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sourceUrl]);

  // Preloads the resolved URL before swapping the active background source.
  useEffect(() => {
    if (!resolvedSrc) {
      setActiveSrc(null);
      previousUrlRef.current = null;
      return undefined;
    }

    if (previousUrlRef.current === resolvedSrc) {
      return undefined;
    }

    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (!cancelled) {
        setActiveSrc(resolvedSrc);
        previousUrlRef.current = resolvedSrc;
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        setActiveSrc(resolvedSrc);
        previousUrlRef.current = resolvedSrc;
      }
    };
    img.src = resolvedSrc;

    return () => {
      cancelled = true;
    };
  }, [resolvedSrc]);

  // ----- RENDER ----- //

  return (
    <div
      className="background-container"
      style={{
        //'--background-glow-color': hexToRgba(theme.highlight, 0.12),
        //'--background-shell-glow': themeMode === ThemeMode.DARK
        //  ? `radial-gradient(circle at top, ${hexToRgba(theme.highlight, 0.12)} 0%, ${theme.background} 52%, ${theme.background} 100%)`
        //  : theme.background,
      }}
    >
      {/* ACTIVE SOURCE 
      {activeSrc && (
        <img
          className="background-image"
          src={activeSrc}
          alt=""
          loading="lazy"
        />
      )}
        */}
    </div>
  );
});

// Dev Tools
Background.displayName = 'Background';

export default Background;
